<template>
  <!--
    data-sidepanel-fetch: this form owns its submission via an explicit fetch
    (see sidepanel_fetch_submit.js) so neither @rails/ujs nor Turbo Drive process
    the submit. That is REQUIRED here: with a normal remote submit, Turbo Drive
    runs a parallel full-page turbo:visit to Review after the redirect -- even
    with data-turbo="false" -- escaping the side panel (proven in-browser).
    Upload CSV and Paste from Clipboard both go through this one path.
  -->
  <form
    :action="createUrl"
    method="post"
    data-sidepanel-fetch="true"
    enctype="multipart/form-data"
    class="activity-import"
    ref="form"
  >
    <input type="hidden" name="activity_import[source]" :value="mode" />

    <!-- Upload CSV (primary) -->
    <section class="activity-import__section">
      <h4 class="activity-import__heading">{{ t.upload_title }}</h4>
      <p class="activity-import__desc">{{ t.upload_description }}</p>

      <div
        class="activity-import__dropzone"
        :class="{ 'is-dragover': dragover, 'has-file': Boolean(fileName) }"
        @dragover.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
        @drop.prevent="onDrop"
        @click="chooseFile"
      >
        <p v-if="fileName" class="activity-import__filename">{{ fileName }}</p>
        <template v-else>
          <p class="activity-import__drophint">{{ t.drop_hint }}</p>
          <span class="activity-import__or">{{ t.or }}</span>
        </template>
        <button
          type="button"
          class="button button--square activity-import__choose"
          @click.stop="chooseFile"
        >
          <span class="button__holder"><span class="button__text">{{ t.choose_file }}</span></span>
        </button>
      </div>
      <p class="activity-import__format">{{ t.supported_format }}</p>

      <input
        type="file"
        name="activity_import[file]"
        accept=".csv"
        ref="fileInput"
        class="is-hidden"
        @change="onFileChange"
      />
    </section>

    <!-- Paste from Clipboard -->
    <section class="activity-import__section">
      <h4 class="activity-import__heading">{{ t.paste_title }}</h4>
      <p class="activity-import__desc">{{ t.paste_description }}</p>

      <textarea
        name="activity_import[raw_data]"
        v-model="pasted"
        rows="6"
        class="input input--full activity-import__textarea"
        :placeholder="t.paste_placeholder"
        @input="onPaste"
      ></textarea>

      <div class="activity-import__paste-meta" v-if="pastePreview">
        <span class="activity-import__counts">{{ pasteSummary }}</span>
        <button type="button" class="button button--square no-space-out activity-import__clear" @click="clearPaste">
          {{ t.clear }}
        </button>
      </div>
    </section>

    <p class="activity-import__help">
      <span class="activity-import__help-prompt">{{ t.help_prompt }}</span>
      <a v-if="helpUrl" :href="helpUrl" target="_blank" rel="noopener" class="activity-import__help-link">{{ t.help_link }}</a>
      <span v-else class="activity-import__help-link is-pending">{{ t.help_link }}</span>
    </p>
  </form>
</template>

<script>
import { preview } from '../tabular_preview';

export default {
  props: {
    createUrl: { type: String, required: true },
    labels: { type: String, default: '{}' },
    helpUrl: { type: String, default: '' },
    // CSS selector of the footer Continue button this component enables/disables.
    submitSelector: { type: String, default: '' },
  },

  data() {
    return { fileName: '', pasted: '', dragover: false };
  },

  computed: {
    t() {
      try {
        return JSON.parse(this.labels);
      } catch (e) {
        return {};
      }
    },

    // Upload and Clipboard are mutually exclusive; whichever holds data wins.
    mode() {
      if (this.fileName) return 'file';
      if (this.pasted.trim() !== '') return 'clipboard';
      return 'file';
    },

    pastePreview() {
      return preview(this.pasted);
    },

    pasteSummary() {
      if (!this.pastePreview) return '';
      return (this.t.paste_summary || '%{rows} rows × %{columns} columns')
        .replace('%{rows}', this.pastePreview.rowCount)
        .replace('%{columns}', this.pastePreview.columnCount);
    },

    // Enabled once a file is chosen OR recognizable data is pasted. Never
    // auto-advances -- the organizer presses Continue (in the footer).
    canContinue() {
      return this.mode === 'file' ? Boolean(this.fileName) : Boolean(this.pastePreview);
    },
  },

  watch: {
    canContinue: {
      immediate: true,
      handler(value) {
        this.$nextTick(() => this.toggleContinue(value));
      },
    },
  },

  methods: {
    toggleContinue(enabled) {
      if (!this.submitSelector) return;
      const button = document.querySelector(this.submitSelector);
      if (button) button.disabled = !enabled;
    },

    chooseFile() {
      this.$refs.fileInput.click();
    },

    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.setFile(file ? file.name : '');
    },

    onDrop(event) {
      this.dragover = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) return;

      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.$refs.fileInput.files = transfer.files;
      this.setFile(file.name);
    },

    // Choosing a file clears any pasted data (mutually exclusive datasets).
    setFile(name) {
      this.fileName = name;
      if (name) this.pasted = '';
    },

    // Pasting data clears any selected file.
    onPaste() {
      if (this.pasted.trim() !== '' && this.fileName) this.clearFile();
    },

    clearFile() {
      this.fileName = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    clearPaste() {
      this.pasted = '';
    },
  },
};
</script>
